<template>
  <div class="modal fade" ref="modalElement" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <font-awesome-icon icon="fas fa-cloud-upload" class="me-2" />
            {{ $t("issuance_reporting.file_upload.title") }}
          </h5>
          <button
            type="button"
            class="btn-close"
            @click="closeModal"
            :disabled="isUploading"
          ></button>
        </div>

        <div class="modal-body">
          <p v-if="!canImport" class="alert alert-subtle-warning">
            {{ $t("issuance_reporting.import_not_trusted") }}
          </p>
          <p class="mb-4">
            {{ $t("issuance_reporting.file_upload.description") }}
          </p>

          <!-- File Upload Area -->
          <div
            class="dropzone dz-clickable d-flex justify-content-center align-items-center px-4 mb-4"
            @dragover.prevent="dragOver"
            @dragleave.prevent="dragLeave"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
            :class="{
              dragging: isDragging && !isUploading && !fileUploaded,
              'bg-light': isUploading,
              disabled: isUploading || !canImport,
            }"
          >
            <input
              ref="fileInput"
              type="file"
              class="form-control visually-hidden"
              :accept="acceptedFileTypes"
              :disabled="!canImport"
              @change="handleFileUpload"
            />

            <div class="dropzone-content text-center">
              <div v-if="!isUploading && !fileUploaded">
                <div class="upload-icon mb-2">
                  <font-awesome-icon icon="fas fa-cloud-upload-alt" size="2x" />
                </div>
                <div>
                  <p class="mb-1 fw-medium">
                    {{ $t("issuance_reporting.file_upload.upload_prompt") }}
                  </p>
                  <small class="text-muted">
                    {{ $t("issuance_reporting.file_upload.supported_formats") }}
                  </small>
                </div>
              </div>
              <LoadingIndicatorComponent
                v-else-if="isUploading"
                inline
                class="justify-content-center"
                :message="$t('issuance_reporting.file_upload.analyzing')"
              />
              <div v-else-if="fileUploaded" class="py-3">
                <font-awesome-icon
                  :icon="
                    importResult?.rejected_count
                      ? 'fas fa-triangle-exclamation'
                      : 'fas fa-check-circle'
                  "
                  class="mb-2"
                  :class="
                    importResult?.rejected_count
                      ? 'text-warning'
                      : 'text-success'
                  "
                  size="2x"
                />
                <p class="mb-1 fw-medium">{{ uploadedFileName }}</p>
              </div>
            </div>
          </div>

          <div
            v-if="importResult"
            role="status"
            class="alert"
            :class="
              importResult.rejected_count
                ? importResult.imported_count
                  ? 'alert-warning'
                  : 'alert-danger'
                : 'alert-success'
            "
          >
            {{
              $t("issuance_reporting.file_upload.import_summary", {
                imported: importResult.imported_count,
                rejected: importResult.rejected_count,
              })
            }}
          </div>
          <details v-if="importResult?.rejected_count" open class="mb-3">
            <summary class="fw-semibold">
              {{ $t("issuance_reporting.file_upload.rejected_records") }}
            </summary>
            <ul class="table-responsive mt-2 mb-0">
              <li
                v-for="rejection in importResult.rejections"
                :key="rejection.record_index"
                class="mb-2"
              >
                <div class="fw-medium">
                  {{
                    $t("issuance_reporting.file_upload.rejected_record", {
                      index: rejection.record_index,
                      device:
                        registryDevices.find(
                          (device) => device.id === rejection.registry_device_id
                        )?.device_name ?? `#${rejection.registry_device_id}`,
                    })
                  }}
                </div>
                <div class="small">
                  {{ formatDateTimeIntoDate(rejection.period_start_at) }} –
                  {{ formatDateTimeIntoDate(rejection.period_end_at) }}
                  <span v-if="rejection.external_id">
                    · {{ rejection.external_id }}</span
                  >
                </div>
                <div>{{ rejection.message }}</div>
              </li>
            </ul>
          </details>
          <div v-if="fileUploaded" class="mt-3">
            <button class="btn btn-outline-primary btn-sm" @click="resetUpload">
              <font-awesome-icon icon="fas fa-sync" class="me-1" />
              {{ $t("issuance_reporting.file_upload.upload_another") }}
            </button>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeModal"
            :disabled="isUploading"
          >
            {{ $t("common.close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { Modal } from "bootstrap";
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import {
  issuanceReportingApi,
  type ProductionImportResponse,
} from "./issuanceReportingApi";
import { useIssuanceProductionDataStore } from "@/dashboard/production_strategy/issuance-reporting/useIssuanceProductionDataStore";
import type { Company, RegistryDevice } from "@/types";
import { onMounted, onUnmounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

const props = defineProps<{
  company: Company;
  registryDevices: RegistryDevice[];
  countryCode: string;
  canImport: boolean;
}>();

const emit = defineEmits<{
  close: [];
  uploaded: [];
}>();

const { t } = useI18n();
const store = useIssuanceProductionDataStore();
const { globalErrorHandler } = useGlobalHandlers();

// Template refs
const modalElement = useTemplateRef<HTMLElement>("modalElement");
const fileInput = useTemplateRef<HTMLInputElement>("fileInput");

// Local state
const isDragging = ref(false);
const isUploading = ref(false);
const fileUploaded = ref(false);
const uploadedFileName = ref("");
const importResult = ref<ProductionImportResponse | null>(null);

// Configuration
const acceptedFileTypes = ".xlsx,.xls,.csv";

// Bootstrap modal instance
let modalInstance: any = null;

// Methods
const showModal = () => {
  if (modalInstance) {
    modalInstance.show();
  }
};

const closeModal = () => {
  if (modalInstance && !isUploading.value) {
    modalInstance.hide();
  }
};

const resetForm = () => {
  importResult.value = null;
  fileUploaded.value = false;
  uploadedFileName.value = "";

  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const dragOver = () => {
  isDragging.value = true;
};

const dragLeave = () => {
  isDragging.value = false;
};

const handleDrop = (event: DragEvent) => {
  isDragging.value = false;

  if (!props.canImport || isUploading.value || fileUploaded.value) {
    return;
  }

  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    handleFile(files[0]);
  }
};

const triggerFileInput = () => {
  if (props.canImport && !isUploading.value && !fileUploaded.value) {
    fileInput.value?.click();
  }
};

const handleFileUpload = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (files && files.length > 0) {
    handleFile(files[0]);
  }
};

const handleFile = async (file: File) => {
  const companyId = props.company.id;
  if (!companyId || !props.canImport || !validateFile(file)) {
    return;
  }

  resetAnalysisState();

  isUploading.value = true;
  uploadedFileName.value = file.name;

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("country_code", props.countryCode);

    const response = await issuanceReportingApi.importFile(companyId, formData);

    if (response.data) {
      importResult.value = response.data;
      fileUploaded.value = true;
      if (response.data.imported_count > 0) {
        await store.fetchIssuanceProductionData(companyId);
        emit("uploaded");
      }
    }
  } catch (error: any) {
    globalErrorHandler(error);
    resetAnalysisState();
  } finally {
    isUploading.value = false;

    if (fileInput.value) {
      fileInput.value.value = "";
    }
  }
};

const resetUpload = () => {
  resetAnalysisState();
};

const validateFile = (file: File): boolean => {
  const allowedTypes = /\.(xlsx?|csv)$/i;
  const maxSizeMb = 25;

  if (!allowedTypes.test(file.name) || file.size / 1_048_576 > maxSizeMb) {
    globalErrorHandler(t("errors.unsupportedFileType"));
    return false;
  }

  return true;
};

const resetAnalysisState = () => {
  importResult.value = null;
  fileUploaded.value = false;
  uploadedFileName.value = "";
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

// Lifecycle
onMounted(() => {
  // Initialize Bootstrap modal
  modalInstance = new Modal(modalElement.value as HTMLElement);

  // Listen for modal events
  modalElement.value?.addEventListener("hidden.bs.modal", () => {
    resetForm();
    emit("close");
  });
});

onUnmounted(() => {
  if (modalInstance) {
    modalInstance.dispose();
  }
});

// Expose modal control
defineExpose({
  show: showModal,
  hide: closeModal,
});
</script>

<style scoped lang="scss">
.dropzone {
  border: 3px dashed var(--bs-secondary);
  border-radius: 10px;
  height: 150px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--bs-body-color);
  transition: background-color 0.2s ease-out;

  &.dragging {
    background-color: var(--bs-secondary-bg-subtle);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:hover:not(.disabled) {
    background-color: var(--phoenix-primary-bg-subtle);
    color: var(--phoenix-primary);
    transition: background-color 0.2s ease-out;
  }

  .upload-icon {
    color: var(--bs-secondary);
  }
}

.modal-lg {
  max-width: 800px;
}

.table-responsive {
  max-height: 300px;
  overflow-y: auto;
}
</style>
